import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, ArrowRight, X } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { getFamilyDefinition } from '../../content';
import { soundManager } from '../../utils/soundManager';
import { hapticManager } from '../../utils/hapticManager';
import { CenteredModal } from '../shared/CenteredModal';
import { MODE_THEME } from '../shared/modeTheme';
interface ChallengeAcceptModalProps {
  isOpen: boolean;
  familyId: string;
  masteryScore: number;
  onAccept: () => void;
  onDecline: () => void;
}
/**
 * ChallengeAcceptModal — Commitment modal before starting a challenge session.
 *
 * Shows the student their current mastery level and the challenge tier they'll face.
 * Part of the learningLab domain (DDD co-locatie s57).
 */
export function ChallengeAcceptModal({
  isOpen,
  familyId,
  masteryScore,
  onAccept,
  onDecline
}: ChallengeAcceptModalProps) {
  const family = getFamilyDefinition(familyId);
  const challengeTheme = MODE_THEME['challenge'];
  if (!isOpen || !family) return null;
  // Map mastery score to the 16-tier system: mastery 70% ≈ tier 11, 80% ≈ tier 13
  const currentTier = Math.min(16, Math.max(1, Math.round(masteryScore / 100 * 16)));
  const challengeTier = Math.min(16, currentTier + 2);
  // Map challengeTier (1-16) to 1-5 stars
  const getStars = (tier: number) => {
    if (tier <= 3) return '⭐';
    if (tier <= 6) return '⭐⭐';
    if (tier <= 9) return '⭐⭐⭐';
    if (tier <= 12) return '⭐⭐⭐⭐';
    return '⭐⭐⭐⭐⭐';
  };
  return <CenteredModal isOpen={isOpen} onClose={onDecline} maxWidth="sm" panelClassName={`bg-white shadow-2xl border-2 ${challengeTheme.borderSoft} rounded-3xl overflow-hidden`} backdropClassName="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" springBounce={0.3} springDuration={0.45} data-id="element-2687">
      {/* Header gradient */}
      <div className={`bg-gradient-to-br ${challengeTheme.gradient} p-6 text-white relative overflow-hidden`} data-id="element-2688">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/4" data-id="element-2689" />
        <button onClick={onDecline} className="absolute top-3 right-3 p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors" aria-label="Sluiten" data-id="element-2690">
          <X className="w-4 h-4 text-white" data-id="element-2691" />
        </button>

        <motion.div initial={{
        scale: 0,
        rotate: -15
      }} animate={{
        scale: 1,
        rotate: 0
      }} transition={{
        type: 'spring',
        bounce: 0.5,
        delay: 0.15
      }} className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4" data-id="element-2692">
          <Trophy className="w-9 h-9 text-white" strokeWidth={2.5} data-id="element-2693" />
        </motion.div>

        <h2 className="text-xl font-black leading-tight mb-1" data-id="element-2694">
          Klaar voor de uitdaging?
        </h2>
        <p className="text-white/80 text-sm font-medium" data-id="element-2695">
          {family.description}
        </p>
      </div>

      {/* Body */}
      <div className="p-6 space-y-4" data-id="element-2696">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-3" data-id="element-2697">
          <div className="bg-slate-50 rounded-2xl p-3 text-center border border-slate-100" data-id="element-2698">
            <div className="text-2xl font-black text-slate-700" data-id="element-2699">
              {Math.round(masteryScore)}%
            </div>
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mt-0.5" data-id="element-2700">
              Jouw niveau
            </div>
          </div>
          <div className={`${challengeTheme.bgSoft} rounded-2xl p-3 text-center border ${challengeTheme.borderSoft}`} data-id="element-2701">
            <div className={`text-2xl font-black ${challengeTheme.textDark}`} data-id="element-2702">
              {getStars(challengeTier)}
            </div>
            <div className={`text-xs font-bold ${challengeTheme.text} uppercase tracking-wide mt-0.5 opacity-70`} data-id="element-2703">
              Hoe moeilijk?
            </div>
          </div>
        </div>

        {/* Message */}
        <p className="text-sm text-slate-600 font-medium text-center leading-relaxed" data-id="element-2704">
          Dit wordt moeilijker dan je gewend bent — maar jij kunt het! 💪
        </p>

        {/* Actions */}
        <div className="space-y-2 pt-1" data-id="element-2705">
          <Button onClick={() => {
          soundManager.playLevelUp();
          hapticManager.hapticMedium();
          onAccept();
        }} variant="primary" size="lg" fullWidth className={`bg-gradient-to-r ${challengeTheme.gradient} border-none shadow-lg ${challengeTheme.shadow} rounded-2xl font-black hover:brightness-110`} data-id="element-2706">
            Start Uitdaging <ArrowRight className="w-4 h-4 ml-2" data-id="element-2707" />
          </Button>
          <Button onClick={onDecline} variant="secondary" size="lg" fullWidth className="rounded-2xl" data-id="element-2708">
            Kies iets anders
          </Button>
        </div>
      </div>
    </CenteredModal>;
}